/**
 * Pure merge helpers for AddToProductMediaMode — extracted to keep the host
 * component file under the 200-line FE limit.
 *
 * `mergeChannelFills` composes the visual slot state per channel by overlaying
 * three sources in increasing priority:
 *
 *   1. Saved occupancy (from useSlotOccupancy)            — base layer
 *   2. Per-slot pending drops (legacy `pendingByChannelSlot`)
 *   3. ReorderPlans (live-fix, takes OVER the channel entirely)
 *
 * When a channel has a plan, the plan is the SOLE source of truth for that
 * channel — saved + per-slot pending are skipped, because the plan already
 * encodes the full final order (existing items moved + new pending inserts).
 *
 * `mergeAiRef` is unchanged from the original — AI-reference slots still flow
 * through the legacy per-slot pending model (no reorder semantics there).
 */
import type { ChannelReorderPlans } from './channelReorderPlan'
import type { ChannelSlotFill, AiRefSlotFill } from './useSlotOccupancy'
import type { SlotFill } from './AddToProductSlotGrid'
import type { AddToProductImage } from '../types'

function mergeSavedIntoOut(
  out: Record<string, Record<number, SlotFill>>,
  saved: Record<string, Record<number, ChannelSlotFill>>,
  planChannels: Set<string>,
): void {
  for (const [chId, slots] of Object.entries(saved)) {
    if (planChannels.has(chId)) continue
    out[chId] = {}
    for (const [slot, fill] of Object.entries(slots)) {
      out[chId][Number(slot)] = {
        url: fill.url,
        linkId: fill.linkId,
        assetId: fill.assetId,
      }
    }
  }
}

function mergePendingIntoOut(
  out: Record<string, Record<number, SlotFill>>,
  pending: Map<string, Map<number, AddToProductImage>>,
  planChannels: Set<string>,
): void {
  for (const [chId, slotMap] of pending.entries()) {
    if (planChannels.has(chId)) continue
    if (!out[chId]) out[chId] = {}
    for (const [slot, image] of slotMap.entries()) {
      out[chId][slot] = { url: image.url, isPending: true, assetId: image.id }
    }
  }
}

function mergePlansIntoOut(
  out: Record<string, Record<number, SlotFill>>,
  plans: ChannelReorderPlans,
): void {
  for (const [chId, plan] of plans.entries()) {
    out[chId] = {}
    plan.forEach((item, idx) => {
      const slot = idx + 1
      if (item.kind === 'existing') {
        out[chId][slot] = {
          url: item.image.url,
          linkId: item.linkId,
          assetId: item.image.id,
        }
      } else {
        out[chId][slot] = {
          url: item.image.url,
          isPending: true,
          assetId: item.image.id,
        }
      }
    })
  }
}

export function mergeChannelFills(
  saved: Record<string, Record<number, ChannelSlotFill>>,
  pending?: Map<string, Map<number, AddToProductImage>>,
  plans?: ChannelReorderPlans,
): Record<string, Record<number, SlotFill>> {
  const out: Record<string, Record<number, SlotFill>> = {}
  const planChannels = new Set(plans?.keys() ?? [])
  mergeSavedIntoOut(out, saved, planChannels)
  if (pending) mergePendingIntoOut(out, pending, planChannels)
  if (plans) mergePlansIntoOut(out, plans)
  return out
}

export function mergeAiRef(
  saved: Record<number, AiRefSlotFill>,
  pending?: Map<number, AddToProductImage>,
): Record<number, SlotFill> {
  const out: Record<number, SlotFill> = {}
  for (const [slot, fill] of Object.entries(saved)) {
    out[Number(slot)] = { url: fill.url, assetId: fill.assetId }
  }
  if (pending) {
    for (const [slot, image] of pending.entries()) {
      out[slot] = { url: image.url, isPending: true, assetId: image.id }
    }
  }
  return out
}
